import {
  Component,
  computed,
  Directive,
  input,
} from '@angular/core';
import { BrnTooltip } from '@spartan-ng/brain/tooltip';
import { cn } from './cn';

// ============================================================
// Separator
// ============================================================
@Component({
  selector: 'hlm-separator',
  standalone: true,
  template: '',
  host: { '[class]': '_c()', role: 'separator' },
})
export class HlmSeparatorComponent {
  readonly orientation = input<'horizontal' | 'vertical'>('horizontal');
  readonly userClass = input<string>('', { alias: 'class' });
  protected _c = computed(() =>
    cn(
      'bg-border',
      this.orientation() === 'horizontal' ? 'h-px w-full' : 'w-px h-full',
      this.userClass(),
    ),
  );
}

// ============================================================
// Badge
// ============================================================
@Component({
  selector: 'hlm-badge',
  standalone: true,
  template: '<ng-content />',
  host: { '[class]': '_c()' },
})
export class HlmBadgeComponent {
  readonly variant = input<'default' | 'secondary' | 'destructive' | 'accent' | 'success' | 'purple' | 'teal' | 'rose' | 'outline' | 'tag'>('default');
  readonly userClass = input<string>('', { alias: 'class' });
  protected _c = computed(() => {
    // Tinted chips use bg-{tone}/15 + text-{tone} (SAME token), per CLAUDE.md
    // Pitfall #13: the *-foreground tokens are designed for SOLID backgrounds and
    // go near-invisible on a transparent tint. Weight is semibold so colored chips
    // stay legible across themes. If you hand-roll a chip, match this recipe exactly
    // (never bg-{tone}/20 + text-{tone}-foreground); prefer routing through this component.
    const variants: Record<string, string> = {
      default: 'bg-primary/20 text-primary',
      secondary: 'bg-secondary text-secondary-foreground',
      destructive: 'bg-destructive/15 text-destructive',
      accent: 'bg-accent/15 text-accent',
      success: 'bg-success/15 text-success',
      purple: 'bg-ring-purple/15 text-ring-purple',
      teal: 'bg-chip-teal/15 text-chip-teal',
      rose: 'bg-chip-rose/15 text-chip-rose',
      outline: 'border border-border text-foreground',
      // User-defined labels (number tags): own sky hue + square-ish shape instead of a pill, so
      // they never read as a reachability type (typeBadgeVariant owns the other tints). Style guide §7.2.
      tag: 'rounded-md border border-chip-sky/35 bg-chip-sky/15 text-chip-sky',
    };
    return cn(
      'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold leading-tight',
      variants[this.variant()],
      this.userClass(),
    );
  });
}

// ============================================================
// Progress (determinate bar)
// ============================================================
@Component({
  selector: 'hlm-progress',
  standalone: true,
  template: `
    <div class="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      @if (value() === null) {
        <div class="h-full w-1/3 animate-indeterminate rounded-full bg-primary"></div>
      } @else {
        <div class="h-full rounded-full bg-primary transition-all" [style.width.%]="clamp(value()!)"></div>
      }
    </div>
  `,
})
export class HlmProgressComponent {
  readonly value = input<number | null>(null);
  protected clamp(v: number): number { return Math.max(0, Math.min(100, v)); }
}

// ============================================================
// Spinner
// ============================================================
@Component({
  selector: 'hlm-spinner',
  standalone: true,
  template: `
    <svg
      [attr.width]="size()" [attr.height]="size()"
      viewBox="0 0 24 24" fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class="animate-spin"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    </svg>
  `,
  host: { '[class]': '_c()' },
})
export class HlmSpinnerComponent {
  readonly size = input('20');
  readonly userClass = input<string>('', { alias: 'class' });
  // inline-flex + leading-none so the spinner sits on the text optical centre
  // inside flex buttons, mirroring the same fix applied to HlmIconComponent.
  protected _c = computed(() =>
    cn('inline-flex shrink-0 items-center justify-center leading-none text-primary', this.userClass()),
  );
}

// ============================================================
// Alert (inline, for forms + info banners)
// ============================================================
@Component({
  selector: 'hlm-alert',
  standalone: true,
  template: '<ng-content />',
  host: { '[class]': '_c()', '[attr.role]': 'role()' },
})
export class HlmAlertComponent {
  readonly tone = input<'info' | 'warn' | 'error' | 'success' | 'neutral'>('info');
  /** `alert` (default) is an assertive live region; pass `note` for static information. */
  readonly role = input<'alert' | 'status' | 'note'>('alert');
  readonly userClass = input<string>('', { alias: 'class' });
  protected _c = computed(() => {
    const tones: Record<string, string> = {
      info: 'border-primary/25 bg-primary/5 text-foreground',
      warn: 'border-accent/50 bg-accent/10 text-foreground',
      error: 'border-destructive/30 bg-destructive/10 text-foreground',
      success: 'border-success/40 bg-success/10 text-foreground',
      // Purely informational — nothing for the operator to act on. Deliberately quieter
      // than warn so it does not compete with the states that do demand an action.
      neutral: 'border-border bg-muted/40 text-foreground',
    };
    return cn(
      'flex items-start gap-2.5 rounded-lg border px-3 py-2 text-sm',
      tones[this.tone()],
      this.userClass(),
    );
  });
}

// ============================================================
// Tooltip — thin wrapper around BrnTooltip (Spartan-NG / CDK Overlay).
// Defaults (delay, position, theme classes) live in app.config.ts via
// provideBrnTooltipDefaultOptions, so call sites only supply the text:
//   <button hlmTooltip="Save changes">…</button>
// CDK Overlay handles body-portal positioning, focus, and aria-describedby
// — escapes every ancestor clip and stacking context (AG-Grid headers,
// row transforms, modals) without the custom JS we used to maintain.
// ============================================================
@Directive({
  selector: '[hlmTooltip]',
  standalone: true,
  hostDirectives: [
    {
      directive: BrnTooltip,
      inputs: ['brnTooltip: hlmTooltip', 'position', 'showDelay', 'hideDelay', 'tooltipDisabled'],
    },
  ],
})
export class HlmTooltipDirective {
  // Behaviour is provided by the BrnTooltip host directive above; the
  // class is a marker that gives the directive its own selector.
}
